import * as React from "react";
import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { useSelector, useStore } from "react-redux";

import axios from 'axios';

import {
    Box,
    Button,
    CssBaseline,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Step,
    StepButton,
    Stepper,
    styled,
    TextField,
    Toolbar,
    Typography,
    useTheme,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Alert,
    Modal,
    Fade
} from "@mui/material";

import Backdrop from '@mui/material/Backdrop';
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CircularProgress from "@mui/material/CircularProgress";

import {
    resetStore,
    loadStore,
    setActiveStep as setActiveStepAction,
    setCompletedSteps as setCompletedStepsAction,
} from "../Store/roiStore.ts";

import { AppLoader } from "./../components/AppLoader.tsx";
import { useWizardSteps } from "../hooks/useWizardSteps.tsx";
import { SAMEDAY_SPEND_KEYS } from "./SameDaySpendDetails.tsx";

import "../styles/app.scss";
import "../styles/pages/roi-calculator.scss";

const ESL_SOLUTION = "Electronic Shelf Labels (ESLs)";

const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};




type Props = {
    roiInfo: {
        fileName: string;
    };
    doReset: boolean,
    onOpenSavedRois: (event: React.MouseEvent<HTMLElement>) => void
    onRestSelectedRoi: () => void
    userName?: string,
    userEmail?: string,
}

export const ROICalculator = forwardRef((props: Props, ref) => {

    const [isValid, setIsValid] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const [showSaveSuccess, setShowSaveSuccess] = useState(false);
    const [showExitWarning, setShowExitWarning] = useState(false);
    const [fileName, setSavedfileName] = useState();
    const [showSaveDialoglLoader, setShowSaveDialoglLoader] = useState(false);

    const [netSuiteStatus, setNetSuiteStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
    const [netSuiteMessage, setNetSuiteMessage] = useState<string>("");
    const [netSuiteFileName, setNetSuiteFileName] = useState<string>("");
    const [netSuiteSendingCover, setNetSuiteSendingCover] = useState<boolean | null>(null);

    const [showPdfError, setShowPdfError] = useState(false);
    const handleClosePdfError = () => setShowPdfError(false);
    const [showEslOnlyWarning, setShowEslOnlyWarning] = useState(false);


    useImperativeHandle(ref, () => ({                
        handleParentSave(ignoreSuccess) {            
            handleSave(ignoreSuccess);
        }
    }))


    const { dispatch } = useStore();

    const steps = useWizardSteps();

    const rawActiveStep = useSelector((state: any) => state.inputFields.activeStep ?? 0);
    const activeStep = Math.min(rawActiveStep, Math.max(0, steps.length - 1));
    const completed = useSelector(
        (state: any) => state.inputFields.completedSteps ?? {}
    ) as { [k: number]: boolean };

    const goToStep = (step: number) => {
        dispatch(setActiveStepAction(step));
    };

    const setCompleted = (value: { [k: number]: boolean }) => {
        dispatch(setCompletedStepsAction(value));
    };

    const handleClose = () => {
        setShowSaveSuccess(false);
        setShowExitWarning(false);
        setNetSuiteStatus("idle");
        setNetSuiteMessage("");
        setNetSuiteFileName("");
        setNetSuiteSendingCover(null);
    };

    const handleResetRoiCalculation = (resetSelectedRoi) => {        
        dispatch(resetStore({}));        
        setShowSaveSuccess(false);

        if (resetSelectedRoi) {
            props.onRestSelectedRoi();
        }
            

        goToStep(0);
        setCompleted({});
    };

    const handleGenerateRoiPdf = () => {
        setShowPdfError(false);

        if (validateRoiCalculation()) {
            setShowSaveDialoglLoader(true);
            axios({
                method: 'POST',
                data: fileName,
                url: "api/Document/GernerateRoiPdf",
                headers: {
                    'Content-Type': 'application/json;charset=UTF-8',
                    /*"Access-Control-Allow-Origin": "*",*/
                },
                responseType: 'arraybuffer',
            })
                .then((res) => {
                    setShowSaveDialoglLoader(false);
                    console.log(res);
                    let blob = new Blob([res.data], { type: 'application/pdf' });
                    downloadFile(blob, "PAR Excellence Automated ROI.pdf");

                })
                .catch(err => {
                    setShowSaveDialoglLoader(false);
                    console.log('Login: ', err);
                });
        }
        else
            setShowPdfError(true);

    };

    const handleSendToNetSuite = (includeCoverPage: boolean) => {
        if (!fileName) return;
        setNetSuiteStatus("sending");
        setNetSuiteSendingCover(includeCoverPage);
        setNetSuiteMessage("");
        setNetSuiteFileName("");
        axios({
            method: "POST",
            url: "api/Document/SendToNetSuite",
            data: {
                fileName,
                includeCoverPage,
                customerName: state.client.name || "Unknown Client",
            },
            headers: { "Content-Type": "application/json;charset=UTF-8" },
        })
            .then((res) => {
                const sentName = res?.data?.fileName;
                setNetSuiteStatus("success");
                setNetSuiteFileName(sentName || "");
                setNetSuiteMessage("Sent to NetSuite file cabinet");
            })
            .catch((err) => {
                const msg = err?.response?.data || err?.message || "An error occurred.";
                setNetSuiteStatus("error");
                setNetSuiteMessage(typeof msg === "string" ? msg : JSON.stringify(msg));
            });
    };

    const downloadFile = (blob, fileName) => {
        const link = document.createElement('a');
        // create a blobURI pointing to our Blob
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        // some browser needs the anchor to be in the doc
        document.body.append(link);
        link.click();
        link.remove();
        // in case the Blob uses a lot of memory
        setTimeout(() => URL.revokeObjectURL(link.href), 7000);
    };

    //const downloadFile = (fileName, data) => {    
    //    // create file in browser       
    //    const json = JSON.stringify(data, null, 2);
    //    const blob = new Blob([json], { type: "application/pdf" });
    //    const href = URL.createObjectURL(blob);

    //    // create "a" HTLM element with href to file
    //    const link = document.createElement("a");
    //    link.href = href;
    //    link.download = fileName + ".pdf";
    //    document.body.appendChild(link);
    //    link.click();

    //    // clean up "a" element & remove ObjectURL
    //    document.body.removeChild(link);
    //    URL.revokeObjectURL(href);
    //}


    let state = useSelector((state: any) => state.inputFields);

    const isSameDayOnly: boolean = useSelector(
        (s: any) => !!s.inputFields.sameDay?.isSameDayOnly
    );

    const isLegacyRoi: boolean = useSelector(
        (s: any) => !!s.inputFields.isLegacyRoi
    );

    const totalSteps = () => {
        return steps.length;
    };

    const completedSteps = () => {
        return Object.keys(completed).length;
    };

    const isLastStep = () => {
        return activeStep === totalSteps() - 1;
    };

    const allStepsCompleted = () => {
        return completedSteps() === totalSteps();
    };

    const handleSave = (ignoreSuccess) => {

        const stateWithUser = {
            ...state,
            savedBy: {
                name: props.userName ?? "",
                email: props.userEmail ?? "",
                savedAt: new Date().toISOString(),
            },
        };

        const json = JSON.stringify(stateWithUser, null, 2);
        const blob = new Blob([json], { type: "application/json" });
        const formData = new FormData();

        formData.append('file', blob as Blob, state.client.name + ".json");

        const solutions = (state.client?.currentParSolutions ?? []).join(", ");
        const roiYear1Pct = String(state.totals?.annualRoiPctYear1 ?? "");
        const annualRecurring = String(state.totals?.solutionSubscriptions
            ? Object.values(state.totals.solutionSubscriptions as Record<string, number>).reduce((a, b) => a + b, 0)
            : "");
        const startupFees = String(state.totals?.oneTimeOnboarding ?? "");
        const repName = encodeURIComponent(props.userName ?? "");
        const queryParams = new URLSearchParams({
            ...(solutions && { solutions }),
            ...(roiYear1Pct && { roiYear1Pct }),
            ...(annualRecurring && { annualRecurring }),
            ...(startupFees && { startupFees }),
            ...(repName && { repName }),
        }).toString();

        axios({
            method: 'POST',
            data: formData,
            url: `api/Storage/Upload${queryParams ? `?${queryParams}` : ""}`,
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        })
            .then((resposne) => {
                if (ignoreSuccess) {
                    handleResetRoiCalculation(true);                    
                }
                else {
                    setSavedfileName(resposne.data.blob.name);
                    setShowSaveSuccess(true);
                }
            })
            .catch((err) => {
                const errorMessage = typeof err.response.data === 'string' ? err.response.data : err.response.data.title;
                console.log(errorMessage);
            });

    };   

    const validateRoiCalculation = () => {
        let totals = state.totals;
        if (!totals) return false;

        let annualRoiTotals = totals.annualRoiTotals;
        let savingsTotals = totals.savingsTotals;

        if (!annualRoiTotals || annualRoiTotals.length <= 1) return false;
        if (!savingsTotals || savingsTotals.length <= 1) return false;

        for (let idx = 1; idx < annualRoiTotals.length; idx++) {
            const v = annualRoiTotals[idx];
            if (v === null || v === undefined || Number.isNaN(Number(v))) {
                return false;
            }
        }

        for (let idx = 1; idx < savingsTotals.length; idx++) {
            const v = savingsTotals[idx];
            if (v === null || v === undefined || Number.isNaN(Number(v))) {
                return false;
            }
        }

        return true;
    }

    const proceedToNextStep = () => {
        const newActiveStep = activeStep + 1;
        setCompleted({ ...completed, [activeStep]: true });
        goToStep(newActiveStep);
    };

    const handleNext = () => {
        const stepKey = steps[activeStep]?.key;
        const selectedSolutions: string[] = state.client.currentParSolutions ?? [];
        const isEslOnlySelection =
            selectedSolutions.length === 1 &&
            selectedSolutions[0] === ESL_SOLUTION;

        if (stepKey === "client" && isEslOnlySelection) {
            setShowEslOnlyWarning(true);
            return;
        }

        proceedToNextStep();
    };

    useEffect(() => {        
        setIsLoading(true);

        if (props.roiInfo) {            
            axios.get("api/Storage/Download?fileName=" + props.roiInfo.fileName)
                .then((response) => {                    
                    console.log(response);
                    dispatch(loadStore(response.data));
                    // Jump to the final ROI step regardless of which wizard variant
                    // (standard vs SameDay-only / mixed) the saved ROI lives in.
                    const lastStep = Math.max(0, steps.length - 1);
                    const completedMap: { [k: number]: boolean } = {};
                    for (let i = 0; i < lastStep; i++) completedMap[i] = true;
                    goToStep(lastStep);
                    setCompleted(completedMap);
                    setIsLoading(false);
                })
                .catch((err) => {
                    setIsLoading(false);
                    const errorMessage = typeof err.response.data === 'string' ? err.response.data : err.response.data.title;
                    console.log(errorMessage);
                });
        }
        else {
            dispatch(resetStore({}));
            goToStep(0);
            setCompleted({});
            setIsLoading(false);
        }
    }, [props.roiInfo]);

    useEffect(() => {

        setIsValid(isValidated(activeStep));

    }, [
        state.client.name,
        state.client.currentParSolutions,
        activeStep,
        state.sameDay?.spend?.[SAMEDAY_SPEND_KEYS.pctJoints],
        state.sameDay?.spend?.[SAMEDAY_SPEND_KEYS.pctSpineTrauma],
        state.sameDay?.spend?.[SAMEDAY_SPEND_KEYS.pctEp],
        state.sameDay?.spend?.[SAMEDAY_SPEND_KEYS.pctRemaining],
    ]);

    useEffect(() => {        
        handleResetRoiCalculation(false);

    }, [props.doReset]);

    const isValidated = (step: number) => {
        const stepKey = steps[step]?.key;
        const spendDetailRows = state.spendDetailsRows;
        const firstRow = spendDetailRows?.row1 ?? {};
        const facilityName = firstRow["Facility Name"] ?? firstRow.Location ?? "";
        let isValid;
        switch (stepKey) {
            case "client":
                isValid =
                    String(state.client.name ?? "").trim().length > 0 &&
                    (state.client.currentParSolutions?.length ?? 0) > 0;
                break;
            case "spendDetails":
                isValid = String(facilityName).length > 0;
                break;
            case "samedaySpend": {
                const sameDaySpend = state.sameDay?.spend ?? {};
                const mixSum =
                    Number(sameDaySpend[SAMEDAY_SPEND_KEYS.pctJoints] ?? 0) +
                    Number(sameDaySpend[SAMEDAY_SPEND_KEYS.pctSpineTrauma] ?? 0) +
                    Number(sameDaySpend[SAMEDAY_SPEND_KEYS.pctEp] ?? 0) +
                    Number(sameDaySpend[SAMEDAY_SPEND_KEYS.pctRemaining] ?? 0);
                isValid = Math.abs(mixSum - 100) < 0.01;
                break;
            }
            default:
                isValid = true;
        }
        setIsValid(isValid);
        return isValid;
    };

    const handleBack = () => {
        goToStep(activeStep - 1);
    };

    const handleStep = (step: number) => () => {
        goToStep(step);
    };

    const handleReset = () => {
        goToStep(0);
        setCompleted({});
    }; 

    return (
        <>
        <div className="container">
            <div className="content">
                {isLegacyRoi && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This ROI was created with the previous pricing model, where PAR
                        subscription fees were entered as <strong>monthly</strong> amounts.
                        It has been opened for reference and still reflects its original
                        figures, but to capture the current <strong>annual</strong> pricing
                        please create a new ROI for this client.
                    </Alert>
                )}
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Stepper nonLinear activeStep={activeStep} alternativeLabel>
                        {steps.map((step, index) => (
                            <Step key={step.name} completed={completed[index]}>
                                <StepButton color="inherit" onClick={handleStep(index)}>
                                    {step.name}
                                </StepButton>
                            </Step>
                        ))}
                    </Stepper>

                    <div style={{ display: "flex", flex: 1 }}>
                        {allStepsCompleted() ? (
                            <>
                                <Typography sx={{ mt: 2, mb: 1 }}>
                                    All steps completed - you&apos;re finished
                                </Typography>
                                <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                                    <Box sx={{ flex: "1 1 auto" }} />
                                    <Button onClick={handleReset}>Reset</Button>
                                </Box>
                            </>
                        ) : (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    flex: 1,
                                }}
                            >
                                <div style={{ height: "100%", padding: "40px" }}>
                                    {steps[activeStep].component}
                                </div>

                                <Box
                                    sx={{
                                        height: "50px",
                                        display: "flex",
                                        flexDirection: "row",
                                        pt: 2,
                                    }}
                                >
                                    <Button
                                        color="inherit"
                                        disabled={activeStep === 0}
                                        onClick={handleBack}
                                        sx={{ fontSize: 18, mr: 1 }}
                                    >
                                        Back
                                    </Button>
                                    <Box sx={{ flex: "1 1 auto" }} />
                                    {!isLastStep() &&
                                        <Button
                                            disabled={!isValid}
                                            onClick={handleNext}
                                            sx={{ fontSize: 18, mr: 1 }}
                                        >
                                            Next
                                        </Button>

                                    }
                                    {isLastStep() &&
                                        <Button
                                            variant="contained"
                                                    sx={{ fontSize: 18, mr: 1 }}
                                                    onClick={(e) => handleSave(false)}
                                        >
                                            Save
                                        </Button>
                                    }
                                </Box>
                            </div>
                        )}
                    </div>
                </Box>
                </div>
                {showSaveSuccess && <Dialog
                    open={showSaveSuccess}
                    onClose={handleClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                    PaperProps={{ className: "save-dialog-paper" }}
                >
                    <DialogTitle id="alert-dialog-title">                        
                        <Alert severity="success">Saved Successfully</Alert>
                    </DialogTitle>
                    <DialogContent sx={{ pb: 0.5 }}>
                        <DialogContentText id="alert-dialog-description">
                            What would you like to do next:
                            <Box
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <Button variant="contained" sx={{ margin: "10px" }} onClick={() => handleResetRoiCalculation(true)}>
                                    New ROI Calculation
                                </Button>
                                <Button variant="contained" sx={{ margin: "10px" }} onClick={handleGenerateRoiPdf}>
                                    {isSameDayOnly ? "Generate PAR SameDay PDF" : "Generate PDF"}
                                </Button>
                                <Button variant="contained" sx={{ margin: "10px" }} onClick={(e) => { props.onRestSelectedRoi(); dispatch(resetStore({})); props.onOpenSavedRois(e);  }}>
                                    Saved ROI Calculation
                                </Button>

                                {/* NetSuite section */}
                                <Box className="netsuite-section">
                                    <Box className="netsuite-section__header">
                                        Send to NetSuite File Cabinet
                                    </Box>
                                    <Box className="netsuite-section__tiles">
                                        {[
                                            { cover: true, label: "With Cover Page", sub: "Full branded report", Icon: MenuBookOutlinedIcon },
                                            { cover: false, label: "Without Cover Page", sub: "Report pages only", Icon: InsertDriveFileOutlinedIcon },
                                        ].map(({ cover, label, sub, Icon }) => {
                                            const isSending = netSuiteStatus === "sending";
                                            const isThisSending = isSending && netSuiteSendingCover === cover;
                                            const tileClass = [
                                                "netsuite-tile",
                                                isSending ? "netsuite-tile--busy" : "",
                                                isSending && !isThisSending ? "netsuite-tile--dim" : "",
                                            ].filter(Boolean).join(" ");
                                            return (
                                                <Box
                                                    key={label}
                                                    role="button"
                                                    className={tileClass}
                                                    onClick={() => !isSending && handleSendToNetSuite(cover)}
                                                >
                                                    {isThisSending
                                                        ? <CircularProgress size={26} className="netsuite-tile__spinner" />
                                                        : <Icon className="netsuite-tile__icon" />}
                                                    <Box className="netsuite-tile__label">{label}</Box>
                                                    <Box className="netsuite-tile__sub">{sub}</Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                    <Box className="netsuite-section__status">
                                        {netSuiteStatus === "sending" && (
                                            <Box className="netsuite-section__status-sending">
                                                Sending to NetSuite…
                                            </Box>
                                        )}
                                        {netSuiteStatus === "success" && (
                                            <>
                                                <Box className="netsuite-section__status-success">
                                                    {`✓ ${netSuiteMessage}`}
                                                </Box>
                                                {netSuiteFileName && (
                                                    <Box className="netsuite-section__filename" title={netSuiteFileName}>
                                                        {netSuiteFileName}
                                                    </Box>
                                                )}
                                            </>
                                        )}
                                        {netSuiteStatus === "error" && (
                                            <Box className="netsuite-section__status-error">
                                                {`✗ ${netSuiteMessage}`}
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                            
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Box className={`save-dialog-actions${showSaveDialoglLoader ? " save-dialog-actions--loading" : ""}`}>
                            {showSaveDialoglLoader && <AppLoader className="save-dialog-loader" />}
                            <Button onClick={handleClose}>Close</Button>
                        </Box>
                    </DialogActions>
                </Dialog>} 
                {showEslOnlyWarning && <Dialog
                    open={showEslOnlyWarning}
                    onClose={() => setShowEslOnlyWarning(false)}
                    aria-labelledby="esl-warning-title"
                    aria-describedby="esl-warning-description"
                >
                    <DialogTitle id="esl-warning-title">
                        <Alert severity="info">ESL-only selection note</Alert>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 0, pb: 0 }}>
                        <DialogContentText id="esl-warning-description">
                            Please note that ESLs alone will not generate an ROI using this tool.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowEslOnlyWarning(false)}>Go Back</Button>
                        <Button
                            onClick={() => {
                                setShowEslOnlyWarning(false);
                                proceedToNextStep();
                            }}
                        >
                            Continue Anyway
                        </Button>
                    </DialogActions>
                </Dialog>}
                {showExitWarning && <Dialog
                    open={showExitWarning}
                    onClose={handleClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">
                        <Alert severity="warning">Confirm Exit!</Alert>
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Are you sure you want to exit the current ROI calculation?                            
                            <Box
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <Button variant="contained" sx={{ margin: "10px" }} onClick={handleResetRoiCalculation}>
                                    Yes
                                </Button>
                                <Button variant="contained" sx={{ margin: "10px" }} onClick={handleGenerateRoiPdf}>
                                    Cancel
                                </Button>                                
                            </Box>
                        </DialogContentText>
                    </DialogContent>                  
                </Dialog>} 
                {showPdfError && <Modal
                    aria-labelledby="transition-modal-title"
                    aria-describedby="transition-modal-description"
                    open={showPdfError}
                    onClose={handleClosePdfError}
                    closeAfterTransition
                    slots={{ backdrop: Backdrop }}
                    slotProps={{
                        backdrop: {
                            timeout: 500,
                        },
                    }}
                >
                    <Fade in={showPdfError}>
                        <Box sx={modalStyle}>
                            <Alert severity="error">{`We can't generate a PDF file for the requested ROI calculation. Please complete all the ROI calculation steps, and try again.`}</Alert>
                        </Box>
                    </Fade>
                </Modal>} 

            </div>
        </>
    );
})


